import React from 'react';
import ReactMarkdown from 'react-markdown';
import { PluggableList } from 'react-markdown/lib/react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import remarkToc from 'remark-toc';
import remarkSlug from 'remark-slug';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import rehypeTruncate from 'rehype-truncate';
import { decodeForMarkdown } from '../../utils/string.utils';
import './Markdown.scss';

interface MarkdownProps {
  content?: string;
  removePageBreaks?: boolean;
  className?: string;
  truncate?: number;
  preserveAngleBrackets?: boolean;
  imageSrcBase?: string;
  generateToc?: boolean;

  /** @deprecated This attribute is only being added for legacy support, as markdown was not available in v1 */
  allowHtml?: boolean;
}

export const Markdown: React.FC<MarkdownProps> = ({
  content,
  removePageBreaks = false,
  className,
  truncate,
  preserveAngleBrackets = false,
  allowHtml = false,
  imageSrcBase,
  generateToc = false,
}) => {
  const withoutPageBreakSymbols = (text: string): string => {
    return (text || '').replace(/\$PAGE_BREAK/g, '');
  };

  const decodedContent = () => {
    let result: string = content || '';
    if (result) {
      if (removePageBreaks) {
        result = withoutPageBreakSymbols(result);
      }
      result = decodeForMarkdown(result, preserveAngleBrackets);
    }

    return result;
  };

  const rehypePlugins: PluggableList = [];
  const remarkPlugins = [remarkGfm];

  if (allowHtml) {
    rehypePlugins.push(rehypeRaw);
    rehypePlugins.push(rehypeSanitize);
  }

  if (truncate) {
    rehypePlugins.push([rehypeTruncate, { maxChars: truncate, ignoreTags: ['a', 'img'] }]);
  }

  if (generateToc) {
    remarkPlugins.push(remarkSlug);
    // @ts-ignore
    remarkPlugins.push(remarkToc);
  }

  return (
    <ReactMarkdown
      className={`markdown-instance ${className}`}
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={{
        a: ({ node, children, ...props }) => {
          if (props.href && /^(?:[a-z+]+:)?\/\//i.test(props.href)) {
            props.target = '_blank';
            props.rel = 'noopener noreferrer';
          }
          return <a {...props}>{children}</a>;
        },
        img: ({ node, ...props }) => {
          // Check if path is relative and convert it to point to your public asset host
          const isAbsolute = /^(?:[a-z+]+:)?\/\//i.test(props.src || '') || (props.src || '').startsWith('data:');
          if (imageSrcBase && props.src && !isAbsolute) {
            const fileName = props.src.replace('./', '');
            props.src = `${imageSrcBase}/${fileName}`;
          }
          return <img {...props} style={{ maxWidth: '100%' }} />;
        },
      }}
    >
      {decodedContent()}
    </ReactMarkdown>
  );
};
